import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, DatePicker, Layout, Row, Select } from 'antd';
import { capitalize, get, omit } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import ResellerServices from 'services/reseller';

import PdCsvDownloader from 'components/pd-csv-downloader';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';

import {
  errHandling,
  formatStatus,
  priceFormat,
  WITHDRAWAL_STATUS_FILTER_LIST,
} from 'utils';
import addDay from 'utils/addDay';

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

export function WithdrawalResellers() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(0);
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [orderList, setOrderList] = useState([]);
  const [startDatePick, setStartDate] = useState('');
  const [status, setStatus] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const dateFormat = 'YYYY/MM/DD';

  const columns = [
    {
      name: 'Date',
      selector: row => row.created_at,
      format: (row) => dayjs(row.created_at).format('DD/MM/YYYY'),
      width: '20%',
      center: true,
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      width: '20%',
      center: true,
      format: (row) => `IDR  ${priceFormat(row.amount)}`,
    },
    {
      name: 'Status',
      selector: row => row.withdrawal.status,
      width: '20%',
      center: true,
      format: (row) => {
        if (row.withdrawal.status) return capitalize(row.withdrawal.status);
        else return '';
      },
    },
    {
      name: 'Reseller Name',
      selector: row => row.reseller.first_name,
      width: '20%',
      center: true,
      format: (row) =>
        row.reseller && row.reseller.first_name && capitalize(row.reseller.first_name),
    },
    {
      name: 'Store Name',
      selector: row => row.reseller.store_name,
      width: '20%',
      center: true,
      format: (row) =>
        row.reseller && row.reseller.store_name && capitalize(row.reseller.store_name),
    },
  ];

  async function fetchWithdrawal({
    endDate,
    page,
    startDate,
    withdrawal_status,
  }) {
    setLoadStatus(true);
    try {
      let end = endDate;
      if (endDate) {
        end = dayjs(endDate, dateFormat).add(1, 'days');
        end = dayjs(end).format(dateFormat);
      }
      const { data } = await ResellerServices.getAllWithdrawal({
        endDate: end,
        page,
        withdrawal_status,
        startDate,
      });
      const withdrawalData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setOrderList(withdrawalData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_withdrawal',
      search: stringified,
    });
  }

  function onChangeDate(dates, [startDate, endDate]) {
    if (startDate === '') delete parsed['start_date'];
    else parsed['start_date'] = startDate;

    if (endDate === '') delete parsed['end_date'];
    else parsed['end_date'] = endDate;

    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_withdrawal',
      search: stringified,
    });
  }

  function filterReseller(param, val) {
    if (!val) delete parsed[param];
    else parsed[param] = val;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_withdrawal',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, [
        'end_date',
        'limit',
        'page',
        'start_date',
        'withdrawal_status',
      ])
    );
    navigate({
      pathname: '/resellers_withdrawal',
      search: stringified,
    });
  }

  async function handleDownloadCsv() {
    let newEndDate = parsed['end_date'];
    if (newEndDate) {
      newEndDate = addDay(parsed['end_date'], 1);
    }
    const { data } = await ResellerServices.getGlobalWithdrawalReport({
      end_date: newEndDate,
      start_date: parsed['start_date'],
      withdrawal_status: parsed['withdrawal_status'],
    });
    const withdrawalData = get(data, 'data', []);
return withdrawalData
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/resellers_withdrawal',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['withdrawal_status']) setStatus(parsed['withdrawal_status']);
    else setStatus('');

    if (parsed['start_date']) setStartDate(parsed['start_date']);
    else setStartDate('');

    if (parsed['end_date']) setEndDate(parsed['end_date']);
    else setEndDate('');

    fetchWithdrawal({
      endDate: parsed['end_date'],
      page: parsed['page'],
      startDate: parsed['start_date'],
      withdrawal_status: parsed['withdrawal_status'],
    });
  }, [
    parsed['end_date'],
    parsed['page'],
    parsed['start_date'],
    parsed['withdrawal_status'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Withdrawal Reseller</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col className="height-32" span={0}>
              <div className="invisible">invisible</div>
            </Col>
            <Col className="mt-1" lg={7}>
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col lg={3} className="mt-1">
              <Select
                className="w-full"
                onChange={filterReseller.bind(this, 'withdrawal_status')}
                value={status}>
                {WITHDRAWAL_STATUS_FILTER_LIST.map((status, i) =>
                  !status ? (
                    <Option key={i} value={status} selected hidden>
                      Status
                    </Option>
                  ) : (
                    <Option key={i} value={status}>
                      {formatStatus(status)}
                    </Option>
                  )
                )}
              </Select>
            </Col>
            <Col className="mt-1" lg={4}>
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>
            <Col lg={{ span: 4, offset: 5 }} className="height-32">
              {orderList.length > 0 && (
                <PdCsvDownloader
                  datas={handleDownloadCsv}
                  filename={'withdrawal-data-homestory'}
            
                />
              )}
            </Col>

            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={orderList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { WithdrawalResellers };
